import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  AGENT_PERMISSION_MODES,
  AGENT_ROLES,
  type AgentPermissionMode,
  type AgentRole,
} from "@paperclip-cloud/shared";
import { authApi } from "../api/queries/auth";
import { auditApi, companiesApi, type Company } from "../api/queries/companies";
import {
  agentHostsApi,
  type AgentHost,
  type AgentHostPatch,
} from "../api/queries/agents";
import { devicesApi, type Device } from "../api/queries/devices";
import { usersApi, type AdminUser } from "../api/queries/users";
import { Modal } from "../components/Modal";
import { toast } from "../components/Toast";
import { useUiStore } from "../stores/ui";
import { ApiError } from "../api/client";
import { formatRelative, formatDate } from "../lib/time";

const PwSchema = z
  .object({
    current: z.string().min(1, "Required"),
    next: z.string().min(8, "At least 8 characters"),
    confirm: z.string().min(1, "Required"),
  })
  .refine((v) => v.next === v.confirm, { message: "Passwords don’t match", path: ["confirm"] });
type PwForm = z.infer<typeof PwSchema>;

const ROLE_LABEL: Record<AgentRole, string> = {
  ceo: "CEO",
  ops: "Operations",
};

const PERMISSION_MODE_LABEL: Record<AgentPermissionMode, string> = {
  ask: "Ask permissions",
  accept_edits: "Accept edits",
  plan: "Plan mode",
  auto: "Auto mode",
  bypass: "Bypass permissions",
};

export function SettingsPage() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const nav = useNavigate();
  const qc = useQueryClient();
  const activeCompanyId = useUiStore((s) => s.activeCompanyId);

  const me = useQuery({ queryKey: ["me"], queryFn: () => authApi.me() });
  const isAdmin = me.data?.user?.is_admin === true;

  const companies = useQuery({ queryKey: ["companies"], queryFn: () => companiesApi.list() });

  const audit = useQuery({
    queryKey: ["audit", activeCompanyId],
    enabled: !!activeCompanyId,
    queryFn: () => auditApi.list(activeCompanyId!, { limit: 50 }),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PwForm>({ resolver: zodResolver(PwSchema) });

  const changePw = useMutation({
    mutationFn: (input: { current: string; next: string }) => authApi.changePassword(input),
    onSuccess: () => {
      toast("Password updated", "success");
      reset();
    },
    onError: (e: ApiError) => setError("root", { message: e.message ?? "Failed to change password" }),
  });

  const logoutEverywhere = useMutation({
    mutationFn: () => authApi.logoutEverywhere(),
    onSuccess: () => nav({ to: "/login" }),
  });

  const logout = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => nav({ to: "/login" }),
  });

  const onSubmitPw = handleSubmit((v) => changePw.mutate({ current: v.current, next: v.next }));

  return (
    <main className="mx-auto max-w-[900px] px-6 py-6">
      <header className="mb-5">
        <h1 className="text-[20px] font-semibold tracking-[-0.01em]">Settings</h1>
      </header>

      <Section title="Theme">
        <div className="flex items-center gap-2">
          {(["dark", "light"] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`btn btn--secondary btn--sm ${
                theme === t ? "border-accent text-accent" : ""
              }`}
              onClick={() => setTheme(t)}
              aria-pressed={theme === t}
            >
              {t}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Account">
        <form onSubmit={onSubmitPw} className="grid max-w-[420px] gap-3" noValidate>
          <label className="field">
            <span className="field__label">Current password</span>
            <input type="password" className={`input${errors.current ? " input--err" : ""}`} {...register("current")} />
            {errors.current && <span className="field__error">{errors.current.message}</span>}
          </label>
          <label className="field">
            <span className="field__label">New password</span>
            <input type="password" className={`input${errors.next ? " input--err" : ""}`} {...register("next")} />
            {errors.next && <span className="field__error">{errors.next.message}</span>}
          </label>
          <label className="field">
            <span className="field__label">Confirm</span>
            <input type="password" className={`input${errors.confirm ? " input--err" : ""}`} {...register("confirm")} />
            {errors.confirm && <span className="field__error">{errors.confirm.message}</span>}
          </label>
          {errors.root && (
            <div role="alert" className="rounded-sm bg-status-error/10 px-3 py-2 text-[12px] text-status-error">
              {errors.root.message}
            </div>
          )}
          <button className="btn btn--primary self-start" disabled={isSubmitting} type="submit">
            Change password
          </button>
        </form>
        <div className="mt-3 flex gap-2">
          <button className="btn btn--secondary btn--sm" onClick={() => logout.mutate()} type="button">
            Sign out
          </button>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => logoutEverywhere.mutate()}
            type="button"
          >
            Sign out everywhere
          </button>
        </div>
      </Section>

      <Section title="Companies">
        <ul className="card divide-y divide-border-subtle">
          {(companies.data?.companies ?? []).map((c) => (
            <CompanyRow key={c.id} company={c} onChanged={() => qc.invalidateQueries({ queryKey: ["companies"] })} />
          ))}
          {companies.data && companies.data.companies.length === 0 && (
            <li className="px-4 py-4 text-secondary">No companies yet.</li>
          )}
        </ul>
      </Section>

      {isAdmin && (
        <Section title="Users">
          <UsersBlock currentUserId={me.data?.user?.id ?? null} />
        </Section>
      )}

      <Section title="Audit log">
        {audit.isLoading ? (
          <div className="grid place-items-center py-6"><span className="spinner" /></div>
        ) : audit.data && audit.data.events.length > 0 ? (
          <div className="card overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="border-b border-border-subtle text-left text-tertiary">
                <tr>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Actor</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                  <th className="px-3 py-2 font-medium">Target</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.events.map((e) => (
                  <tr key={e.id} className="border-b border-border-subtle last:border-b-0">
                    <td className="px-3 py-2 font-mono text-tertiary" title={formatDate(e.at)}>
                      {formatRelative(e.at)}
                    </td>
                    <td className="px-3 py-2 text-secondary">{e.actor_email}</td>
                    <td className="px-3 py-2 font-mono text-primary">{e.action}</td>
                    <td className="px-3 py-2 font-mono text-tertiary">
                      {e.target_type}/{e.target_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card px-4 py-6 text-secondary">No audit events.</div>
        )}
      </Section>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="micro mb-2">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Hub-wide user management. Visible only when `me.is_admin` is true. Lists
 * every user in the hub, lets the admin create new ones (with a
 * server-generated or chosen password), disable existing ones, or toggle
 * admin rights. The "last admin" is protected — the hub refuses to
 * disable or demote the only admin row.
 */
function UsersBlock({ currentUserId }: { currentUserId: string | null }) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [reveal, setReveal] = useState<{ email: string; password: string } | null>(null);

  const usersQ = useQuery({ queryKey: ["admin-users"], queryFn: () => usersApi.list() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-users"] });

  const disable = useMutation({
    mutationFn: (id: string) => usersApi.disable(id),
    onSuccess: () => {
      toast("User disabled");
      invalidate();
    },
    onError: (e: ApiError) => toast(e.message ?? "Failed to disable", "error"),
  });

  const enable = useMutation({
    mutationFn: (u: AdminUser) => usersApi.enable(u.id),
    onSuccess: (res, u) => {
      if (res.generated_password) {
        setReveal({ email: u.email, password: res.generated_password });
      } else {
        toast("User enabled");
      }
      invalidate();
    },
    onError: (e: ApiError) => toast(e.message ?? "Failed to enable", "error"),
  });

  const togglePromote = useMutation({
    mutationFn: ({ id, makeAdmin }: { id: string; makeAdmin: boolean }) =>
      usersApi.patch(id, { is_admin: makeAdmin }),
    onSuccess: (_r, vars) => {
      toast(vars.makeAdmin ? "Promoted to admin" : "Removed admin rights");
      invalidate();
    },
    onError: (e: ApiError) => toast(e.message ?? "Failed", "error"),
  });

  const onAdded = (email: string, password: string | null) => {
    setShowAdd(false);
    invalidate();
    if (password) setReveal({ email, password });
    else toast("User added");
  };

  const users = usersQ.data?.users ?? [];

  return (
    <>
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <p className="text-[12px] text-tertiary">
            People who can sign in to this hub. Disabling a user revokes
            every active session for them and clears their password — they
            won't be able to log in until you re-enable.
          </p>
          <button
            type="button"
            className="btn btn--primary btn--sm shrink-0"
            onClick={() => setShowAdd(true)}
          >
            Add user
          </button>
        </div>
        {usersQ.isLoading ? (
          <div className="grid place-items-center py-6">
            <span className="spinner" />
          </div>
        ) : users.length === 0 ? (
          <div className="px-4 py-6 text-secondary">No users yet.</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="border-b border-border-subtle text-left text-tertiary">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Created</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === currentUserId;
                return (
                  <tr key={u.id} className="border-b border-border-subtle last:border-b-0">
                    <td className="px-3 py-2 text-primary">{u.display_name}</td>
                    <td className="px-3 py-2 text-secondary">{u.email}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          u.is_admin
                            ? "rounded-sm bg-accent/15 px-2 py-[1px] text-[11px] text-accent"
                            : "text-tertiary"
                        }
                      >
                        {u.is_admin ? "admin" : "user"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {u.is_disabled ? (
                        <span className="rounded-sm bg-status-error/15 px-2 py-[1px] text-[11px] text-status-error">
                          disabled
                        </span>
                      ) : (
                        <span className="text-tertiary">active</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-tertiary" title={formatDate(u.created_at)}>
                      {formatRelative(u.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        {!isSelf && (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => togglePromote.mutate({ id: u.id, makeAdmin: !u.is_admin })}
                            disabled={togglePromote.isPending}
                          >
                            {u.is_admin ? "Remove admin" : "Make admin"}
                          </button>
                        )}
                        {u.is_disabled ? (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => enable.mutate(u)}
                            disabled={enable.isPending}
                          >
                            Enable
                          </button>
                        ) : !isSelf ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm text-status-error"
                            onClick={() => {
                              if (window.confirm(`Disable ${u.email}? They will be signed out everywhere.`))
                                disable.mutate(u.id);
                            }}
                            disabled={disable.isPending}
                          >
                            Disable
                          </button>
                        ) : (
                          <span className="text-[11px] text-tertiary">(you)</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <AddUserModal open={showAdd} onClose={() => setShowAdd(false)} onAdded={onAdded} />
      <RevealPasswordModal
        open={reveal !== null}
        email={reveal?.email ?? ""}
        password={reveal?.password ?? ""}
        onClose={() => setReveal(null)}
      />
    </>
  );
}

const AddUserSchema = z.object({
  email: z.string().email("Not a valid email"),
  display_name: z.string().min(1, "Required").max(120),
  password: z.string().max(256).optional().or(z.literal("")),
  is_admin: z.boolean().optional(),
});
type AddUserForm = z.infer<typeof AddUserSchema>;

function AddUserModal({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (email: string, generatedPassword: string | null) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<AddUserForm>({
    resolver: zodResolver(AddUserSchema),
    defaultValues: { is_admin: false },
  });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const create = useMutation({
    mutationFn: (v: AddUserForm) =>
      usersApi.create({
        email: v.email,
        display_name: v.display_name,
        password: v.password && v.password.length >= 8 ? v.password : undefined,
        is_admin: v.is_admin,
      }),
    onSuccess: (res) => onAdded(res.user.email, res.generated_password),
    onError: (e: ApiError) =>
      setError("root", { message: e.message ?? "Could not create user" }),
  });

  const onSubmit = handleSubmit((v) => {
    if (v.password && v.password.length > 0 && v.password.length < 8) {
      setError("password", { message: "Min 8 characters (or leave blank to generate)" });
      return;
    }
    create.mutate(v);
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add user"
      size="sm"
      footer={
        <>
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            Create
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-3" noValidate>
        <label className="field">
          <span className="field__label">Email</span>
          <input
            type="email"
            autoComplete="off"
            className={`input${errors.email ? " input--err" : ""}`}
            {...register("email")}
          />
          {errors.email && <span className="field__error">{errors.email.message}</span>}
        </label>
        <label className="field">
          <span className="field__label">Display name</span>
          <input
            type="text"
            autoComplete="off"
            className={`input${errors.display_name ? " input--err" : ""}`}
            {...register("display_name")}
          />
          {errors.display_name && <span className="field__error">{errors.display_name.message}</span>}
        </label>
        <label className="field">
          <span className="field__label">Password (leave blank to generate)</span>
          <input
            type="text"
            autoComplete="new-password"
            className={`input${errors.password ? " input--err" : ""}`}
            placeholder="auto-generated"
            {...register("password")}
          />
          {errors.password && <span className="field__error">{errors.password.message}</span>}
        </label>
        <label className="inline-flex items-center gap-2 text-[12px] text-secondary">
          <input type="checkbox" {...register("is_admin")} />
          Grant admin rights (can manage users)
        </label>
        {errors.root && (
          <div role="alert" className="rounded-sm bg-status-error/10 px-3 py-2 text-[12px] text-status-error">
            {errors.root.message}
          </div>
        )}
      </form>
    </Modal>
  );
}

function RevealPasswordModal({
  open,
  email,
  password,
  onClose,
}: {
  open: boolean;
  email: string;
  password: string;
  onClose: () => void;
}) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast("Password copied");
    } catch {
      toast("Couldn't copy — select and copy manually", "error");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="One-time password"
      size="sm"
      footer={
        <>
          <button type="button" className="btn btn--secondary btn--sm" onClick={copy}>
            Copy
          </button>
          <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <div className="grid gap-3 text-[12px]">
        <p className="text-secondary">
          Share this with <strong className="text-primary">{email}</strong> through a
          secure channel. They should change it after the first sign-in.
          <br />
          <span className="text-tertiary">This is shown once — close this dialog and it's gone.</span>
        </p>
        <code className="block break-all rounded-sm border border-border-subtle bg-sunken px-3 py-2 font-mono text-[13px] text-primary">
          {password}
        </code>
      </div>
    </Modal>
  );
}

function CompanyRow({ company, onChanged }: { company: Company; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(company.name);
  const setActive = useUiStore((s) => s.setActiveCompanyId);
  const activeId = useUiStore((s) => s.activeCompanyId);

  const rename = useMutation({
    mutationFn: () => companiesApi.rename(company.id, name),
    onSuccess: () => {
      toast("Renamed");
      setEditing(false);
      onChanged();
    },
    onError: (e: Error) => toast(e.message ?? "Rename failed", "error"),
  });

  const remove = useMutation({
    mutationFn: () =>
      company.role === "owner" ? companiesApi.remove(company.id) : companiesApi.leave(company.id),
    onSuccess: () => {
      toast(company.role === "owner" ? "Company deleted" : "You left the company");
      onChanged();
    },
    onError: (e: ApiError) => toast(e.message ?? "Failed", "error"),
  });

  return (
    <li className="flex flex-col gap-3 px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          ) : (
            <div className="text-[13px] font-semibold text-primary">{company.name}</div>
          )}
          <div className="text-[11px] text-tertiary">
            {company.role} · {company.device_count} device{company.device_count === 1 ? "" : "s"}
          </div>
        </div>
        {activeId !== company.id && (
          <button className="btn btn--ghost btn--sm" onClick={() => setActive(company.id)} type="button">
            Switch to
          </button>
        )}
        {editing ? (
          <>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setEditing(false)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              onClick={() => rename.mutate()}
              disabled={rename.isPending || !name.trim()}
              type="button"
            >
              Save
            </button>
          </>
        ) : (
          <button
            className="btn btn--secondary btn--sm"
            onClick={() => setEditing(true)}
            type="button"
          >
            Rename
          </button>
        )}
        <button
          className="btn btn--ghost btn--sm text-status-error"
          onClick={() => {
            if (
              window.confirm(
                company.role === "owner"
                  ? "Delete this company? This cannot be undone."
                  : "Leave this company?",
              )
            )
              remove.mutate();
          }}
          type="button"
        >
          {company.role === "owner" ? "Delete" : "Leave"}
        </button>
      </div>
      <AgentHostsBlock company={company} />
    </li>
  );
}

/**
 * Per-company agent host list. One editable card per role (CEO, Ops). The
 * host is the device that runs the local Claude CLI for that role; the
 * permission mode controls whether the SDK prompts the user before each
 * tool call.
 *
 * Owner/admin only. Other roles see a read-only summary.
 */
function AgentHostsBlock({ company }: { company: Company }) {
  const canEdit = company.role === "owner" || company.role === "admin";

  const hostsQ = useQuery({
    queryKey: ["agent-hosts", company.id],
    queryFn: () => agentHostsApi.list(company.id),
  });

  const devicesQ = useQuery({
    queryKey: ["devices", company.id],
    queryFn: () => devicesApi.list(company.id),
  });

  const hosts = hostsQ.data?.agent_hosts ?? [];
  const devices = devicesQ.data?.devices ?? [];
  const byRole = new Map<AgentRole, AgentHost>();
  for (const h of hosts) byRole.set(h.role, h);

  return (
    <div className="rounded-sm border border-border-subtle bg-sunken">
      <div className="border-b border-border-subtle px-3 py-2">
        <p className="text-[11px] text-tertiary">
          Each agent runs on whichever paired computer you assign. The CEO host
          is where the local <code className="font-mono">claude</code> CLI
          executes; the Ops host where the operations manager runs. They can be
          the same machine.
        </p>
      </div>
      <ul className="divide-y divide-border-subtle">
        {AGENT_ROLES.map((role) => {
          const host = byRole.get(role);
          return (
            <li key={role} className="px-3 py-3">
              <AgentHostRow
                companyId={company.id}
                role={role}
                host={host}
                devices={devices}
                defaultModel={company.ceo_model}
                canEdit={canEdit}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface AgentHostFormState {
  device_id: string;
  permission_mode: AgentPermissionMode;
  enabled: boolean;
  model: string;
}

function AgentHostRow({
  companyId,
  role,
  host,
  devices,
  defaultModel,
  canEdit,
}: {
  companyId: string;
  role: AgentRole;
  host?: AgentHost;
  devices: Device[];
  defaultModel: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();

  const initial: AgentHostFormState = {
    device_id: host?.device_id ?? "",
    // Default permission mode for new agent rows = "ask" (matches hub default).
    permission_mode: host?.permission_mode ?? "ask",
    enabled: host?.enabled ?? true,
    model: host?.model ?? defaultModel,
  };

  const [form, setForm] = useState<AgentHostFormState>(initial);

  // When the server returns a fresh host row, sync the form. This also covers
  // the case where the row is created on first PATCH.
  useEffect(() => {
    setForm({
      device_id: host?.device_id ?? "",
      permission_mode: host?.permission_mode ?? "ask",
      enabled: host?.enabled ?? true,
      model: host?.model ?? defaultModel,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host?.id, host?.device_id, host?.permission_mode, host?.enabled, host?.model]);

  const dirty =
    form.device_id !== (host?.device_id ?? "") ||
    form.permission_mode !== (host?.permission_mode ?? "ask") ||
    form.enabled !== (host?.enabled ?? true) ||
    form.model !== (host?.model ?? defaultModel);

  const save = useMutation({
    mutationFn: (patch: AgentHostPatch) => agentHostsApi.update(companyId, role, patch),
    onSuccess: () => {
      toast(`${ROLE_LABEL[role]} host updated`);
      qc.invalidateQueries({ queryKey: ["agent-hosts", companyId] });
    },
    onError: (e: ApiError) => toast(e.message ?? "Failed to update agent host", "error"),
  });

  const onSave = () => {
    save.mutate({
      device_id: form.device_id || null,
      permission_mode: form.permission_mode,
      enabled: form.enabled,
      model: form.model,
    });
  };

  if (!canEdit) {
    const dev = devices.find((d) => d.id === host?.device_id);
    return (
      <div className="grid grid-cols-[120px_1fr] items-baseline gap-3 text-[12px]">
        <span className="micro">{ROLE_LABEL[role]}</span>
        <div className="text-secondary">
          {host && host.enabled ? (
            <>
              {dev?.name ?? "—"} · {PERMISSION_MODE_LABEL[host.permission_mode]} ·{" "}
              <span className="font-mono text-tertiary">{host.model}</span>
            </>
          ) : (
            <span className="text-tertiary">disabled</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[120px_1fr_auto] items-start gap-3 text-[12px]">
      <div>
        <div className="text-[13px] font-semibold text-primary">{ROLE_LABEL[role]}</div>
        <label className="mt-1 inline-flex items-center gap-2 text-[11px] text-tertiary">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          Enabled
        </label>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <label className="field">
          <span className="field__label">Device</span>
          <select
            className="input"
            value={form.device_id}
            onChange={(e) => setForm((f) => ({ ...f, device_id: e.target.value }))}
          >
            <option value="">— none —</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id} disabled={d.pairing_state === "revoked"}>
                {d.name}
                {d.online ? "" : " (offline)"}
                {d.pairing_state === "revoked" ? " (revoked)" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Permission mode</span>
          <select
            className="input"
            value={form.permission_mode}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                permission_mode: e.target.value as AgentPermissionMode,
              }))
            }
          >
            {AGENT_PERMISSION_MODES.map((m) => (
              <option key={m} value={m}>
                {PERMISSION_MODE_LABEL[m]}
              </option>
            ))}
          </select>
        </label>

        <label className="field sm:col-span-2">
          <span className="field__label">Model</span>
          <input
            className="input"
            value={form.model}
            placeholder={defaultModel}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
          />
        </label>
      </div>

      <button
        className="btn btn--primary btn--sm self-end"
        type="button"
        onClick={onSave}
        disabled={!dirty || save.isPending}
      >
        Save
      </button>
    </div>
  );
}
